import { useEffect, useRef } from "react";

import { serverUpdateTimerDuration } from "../../api/api-server-actions";
import { playTickingAudio, playTimerEndAudio, stopTickingAudio } from "../audio";

import { useTimeRemaining } from "./use-time-remaining";

import { useAppSelector } from "store/index";
import { selectCurrentClassUserIsInClassSession } from "store/selectors/class-user";

const TICKING_SOUND_STARTS_AT_MS = 12 * 1000;
// We play the timer finished sound slightly before the timer ends so that the noise better coincides with the finish.
const TIMER_END_SOUND_STARTS_AT_MS = 1 * 1000;

interface UseTimerEndObserverProps {
  timerEndsAt: string | null | undefined;
  classSessionId: string | undefined;
  canAuthorSection: boolean;
}

export const useTimerEndObserver = ({
  timerEndsAt,
  classSessionId,
  canAuthorSection,
}: UseTimerEndObserverProps) => {
  const isInClassSession = useAppSelector(selectCurrentClassUserIsInClassSession);
  const timerEndDate = timerEndsAt ? new Date(timerEndsAt) : null;
  const { timeRemainingMs } = useTimeRemaining(timerEndDate);
  const previousTimeRemainingMs = useRef<number | null>(null);

  const timeToEndTheTimerHasPassed =
    previousTimeRemainingMs.current !== null && timeRemainingMs === null;
  const timeToStartTickingHasPassed = !!(
    timeRemainingMs && timeRemainingMs < TICKING_SOUND_STARTS_AT_MS
  );
  const timeToStartTimerEndSoundHasPassed = !!(
    timeRemainingMs && timeRemainingMs < TIMER_END_SOUND_STARTS_AT_MS
  );

  useEffect(() => {
    previousTimeRemainingMs.current = timeRemainingMs;
  }, [timeRemainingMs]);

  useEffect(() => {
    if (!isInClassSession) return;

    if (timeToStartTickingHasPassed) {
      playTickingAudio();
    } else {
      stopTickingAudio();
    }
    if (timeToStartTimerEndSoundHasPassed) {
      playTimerEndAudio();
    }
    return () => stopTickingAudio();
  }, [timeToStartTickingHasPassed, timeToStartTimerEndSoundHasPassed, isInClassSession]);

  useEffect(() => {
    if (timeToEndTheTimerHasPassed && canAuthorSection && classSessionId) {
      stopTickingAudio();
      serverUpdateTimerDuration(classSessionId, 0);
    }
  }, [timeToEndTheTimerHasPassed, canAuthorSection, classSessionId]);
};
